import styled from '@emotion/styled';
import { aspectRatioPaddings, AspectRatios } from '@model/common/image';
import { withTheme } from '@emotion/react';
import React, { useState, useRef, useEffect } from 'react';
import { Theme } from '@theme/base';
import { breakpoints } from '@styles/breakpoints';
import { TestId } from '@components/test-ids';
import { LazyLoadBackgroundImage } from '../lazy-load';
import { keyframes } from '@emotion/css';

export interface ImageCrossFadeProps {
  images?: Array<string>;
  theme: Theme;
  triggerPlay?: boolean;
  fadeDurationMs?: number;
  slideDisplayDurationMs?: number;
  width?: number;
  height?: number;
  aspectRatio?: AspectRatios;
  slideshowTriggerDelayMs?: number;
  testId?: string;
  className?: string;
  sizes?: string;
}

/* ************
 *   Styles   *
 **************/

const crossfadeAnimation = (index: number, totalSlides: number, slideDisplayDuration: number, fadeDuration: number) => {
  const slideTimePercentage = 100 / totalSlides;
  const transitionPercentage = (slideTimePercentage * fadeDuration) / slideDisplayDuration;
  const startPercentage = slideTimePercentage * index;

  const fadeInStart: number = startPercentage - transitionPercentage / 2;
  const fadeInEnd: number = startPercentage + transitionPercentage / 2;
  const fadeOutBegin: number =
    startPercentage +
    slideTimePercentage -
    (startPercentage + slideTimePercentage + transitionPercentage / 2 > 100
      ? transitionPercentage
      : transitionPercentage / 2);
  const fadeOutEnd: number =
    startPercentage + slideTimePercentage + transitionPercentage / 2 > 100
      ? 100
      : startPercentage + slideTimePercentage + transitionPercentage / 2;

  return keyframes`
  0%, ${fadeInStart}% {
    opacity: 0;
  }
  ${fadeInEnd}%, ${fadeOutBegin}% {
    opacity: 1
  }
  ${fadeOutEnd}% {
    opacity: 0
  }
`;
};

const Container: any = styled.div((props: any) => ({
  position: 'relative',
  width: props.width && !props.hasAspectRatio ? props.width : '100%',
  height: props.hasAspectRatio ? 'auto' : props.height || '100%',
  paddingTop: props.hasAspectRatio ? aspectRatioPaddings[props.aspectRatio] : 0,
  img: {
    WebkitUserSelect: 'none' /* disable selection/Copy of UIWebView */,
    WebkitTouchCallout: 'none' /* disable the IOS popup when long-press on a link */
  }
}));

const BaseImage: any = styled(LazyLoadBackgroundImage)({});

export const SlideshowImage: any = styled.div(
  ({
    imageURL,
    index,
    totalSlides,
    slideDisplayDuration,
    fadeDuration,
    playSlideshow
  }: {
    imageURL: string;
    index: number;
    totalSlides: number;
    slideDisplayDuration: number;
    fadeDuration: number;
    playSlideshow: boolean;
  }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    contentVisibility: 'auto',
    backgroundImage: playSlideshow ? `url(${imageURL})` : 'none',
    opacity: 0,
    animation:
      totalSlides > 1 && playSlideshow
        ? `${crossfadeAnimation(index, totalSlides, slideDisplayDuration, fadeDuration)} ${
            totalSlides * slideDisplayDuration
          }s infinite`
        : 'none',
    animationDelay: `-${slideDisplayDuration}s`,
    animationTimingFunction: 'linear'
  })
);

const SlideshowContainer: any = styled.div(
  ({
    show,
    fadeDuration,
    slideshowTriggerDelay
  }: {
    show: boolean;
    fadeDuration: number;
    slideshowTriggerDelay: number;
  }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: show ? 1 : 0,
    transition: `opacity ${fadeDuration}s linear ${slideshowTriggerDelay}s`
  })
);

const getImageWidth = (size?: string) => {
  switch (size) {
    case '100vw':
      return breakpoints.xLarge;
    case '33vw':
      return breakpoints.medium;
    default:
      return breakpoints.small;
  }
};

export const ImageCrossfadeComponent = ({
  triggerPlay,
  images,
  width,
  height,
  aspectRatio,
  slideDisplayDurationMs,
  fadeDurationMs,
  slideshowTriggerDelayMs,
  className,
  testId,
  sizes
}: ImageCrossFadeProps) => {
  /* *** STATE *** */
  const [playSlideshow, setPlaySlideshow] = useState<boolean>(false);

  /* *** REF *** */
  const timer = useRef<any>(null);

  /* *** HOOKS *** */
  useEffect(() => {
    if (triggerPlay) {
      setPlaySlideshow(true);
    } else {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => setPlaySlideshow(false), fadeDurationMs! + slideshowTriggerDelayMs!);
    }

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [triggerPlay]);

  const imageURLs: Array<string> = images || [];

  const slideDisplayDuration = slideDisplayDurationMs! / 1000;
  const fadeDuration = fadeDurationMs! / 1000;
  const slideshowTriggerDelay = slideshowTriggerDelayMs! / 1000;
  const crossfadeImageWidth = getImageWidth(sizes);

  if (!imageURLs || !imageURLs.length) {
    return (
      <Container
        data-testid={testId || TestId.imageCrossfade.main}
        aspectRatio={AspectRatios['16:9']}
        hasAspectRatio={true}
      />
    );
  }

  const hasAspectRatio = aspectRatio && aspectRatio !== AspectRatios.NONE;

  const shouldRenderSlideshow = !!imageURLs?.length;

  /* *** RENDERERS *** */
  const renderSlideshowImages = () =>
    imageURLs.map((imageURL: string, index: number) => (
      <SlideshowImage
        key={`${index}-${imageURL}`}
        testId={TestId.imageCrossfade.slideshowImage}
        imageURL={`${imageURL}?w=${crossfadeImageWidth}`}
        index={index}
        totalSlides={imageURLs.length}
        slideDisplayDuration={slideDisplayDuration}
        fadeDuration={fadeDuration}
        playSlideshow={playSlideshow}
      />
    ));

  return (
    <Container
      data-testid={testId || TestId.imageCrossfade.main}
      width={width}
      height={height}
      className={className}
      aspectRatio={aspectRatio}
      hasAspectRatio={hasAspectRatio}
    >
      <BaseImage testId={TestId.imageCrossfade.baseImage} src={imageURLs[0]} sizes={sizes} />
      {imageURLs.length > 1 && shouldRenderSlideshow && triggerPlay && (
        <SlideshowContainer
          data-testid={TestId.imageCrossfade.slideshowContainer}
          show={triggerPlay}
          fadeDuration={fadeDuration}
          slideshowTriggerDelay={slideshowTriggerDelay}
        >
          {renderSlideshowImages()}
        </SlideshowContainer>
      )}
    </Container>
  );
};

ImageCrossfadeComponent.defaultProps = {
  fadeDurationMs: 300,
  slideDisplayDurationMs: 1500,
  slideshowTriggerDelayMs: 100,
  triggerPlay: false
};

export const ImageCrossfade = withTheme(ImageCrossfadeComponent);
